import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, CalendarClock, Scale } from "lucide-react";
import { LineReveal, Reveal, Kicker } from "../components/Reveal";
import { Marquee } from "../components/Marquee";
import { ContactForm } from "../components/ContactForm";
import { ElmMark } from "../components/Logo";
import { CALENDLY_URL, PRACTICES, WHY_AEA } from "../data/content";

const MARQUEE_ITEMS = [
  { label: "Compliance", to: "/compliance-consulting" },
  { label: "Capital Markets", to: "/capital-markets-consulting" },
  { label: "Trust Services", to: "/trust-services-consulting" },
  { label: "SEC", to: "/compliance-consulting" },
  { label: "FINRA", to: "/compliance-consulting" },
  { label: "Rule 506(b)", to: "/capital-markets-consulting" },
  { label: "Rule 506(c)", to: "/capital-markets-consulting" },
  { label: "Fiduciary Governance", to: "/trust-services-consulting" },
  { label: "Expert Witness", to: "/expert-witness" },
  { label: "AML Independent Audits", to: "/aml-independent-audits" },
  { label: "Online Platform Launches", to: "/aml-independent-audits" },
];

const OfficeBand = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [26, -26]);
  return (
    <section ref={ref} className="py-20 md:py-28 border-t border-[#0A2B3D]/10 overflow-hidden" data-testid="office-band">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <Reveal className="lg:col-span-5">
          <Kicker>Portsmouth, New Hampshire</Kicker>
          <h2 className="mt-5 font-serif font-bold text-3xl sm:text-4xl lg:text-5xl text-[#0A2B3D] tracking-tight leading-[1.15]" data-testid="office-caption">
            Under the arch on <em className="italic text-[#3E9BDC]">Congress Street.</em>
          </h2>
          <p className="mt-6 text-[#475569] leading-relaxed max-w-md">
            Our offices sit in the heart of downtown Portsmouth — 104 Congress Street, Suite 404 —
            in one of New England's most walkable, historic districts.
          </p>
        </Reveal>
        <div className="lg:col-span-7 lg:pl-10">
          <Reveal delay={0.12}>
            <div className="relative">
              <div className="absolute -top-4 -left-4 w-full h-full border border-[#3E9BDC]/40" aria-hidden="true" />
              <div className="relative overflow-hidden">
                <motion.img
                  src="/assets/portsmouth-arch.jpg"
                  alt="The Congress Street arch in downtown Portsmouth, New Hampshire, home of American Elm Advisors"
                  className="w-full aspect-[4/3] object-cover object-[50%_35%] scale-110"
                  style={{ y }}
                  data-testid="office-image"
                />
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

const Home = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const gridY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const markY = useTransform(scrollYProgress, [0, 1], [0, 200]);

  return (
    <div data-testid="home-page">
      <section ref={heroRef} className="relative bg-[#0A2B3D] text-white overflow-hidden" data-testid="home-hero">
        <motion.div
          aria-hidden="true"
          className="absolute inset-0"
          style={{
            y: gridY,
            backgroundImage: "linear-gradient(rgba(169,207,226,0.07) 1px, transparent 1px), linear-gradient(90deg, rgba(169,207,226,0.07) 1px, transparent 1px)",
            backgroundSize: "72px 72px",
          }}
        />
        <motion.div aria-hidden="true" className="absolute -right-16 top-1/2 -translate-y-1/2 opacity-[0.08]" style={{ y: markY }}>
          <ElmMark className="w-[420px] h-[420px]" light />
        </motion.div>

        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 md:px-12 pt-28 pb-24 md:pt-40 md:pb-32">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}>
            <Kicker light>American Elm Advisors · Portsmouth, NH</Kicker>
          </motion.div>

          <h1 className="mt-8 font-serif font-bold tracking-tight leading-[1.05] text-5xl sm:text-6xl lg:text-7xl" data-testid="hero-headline">
            <LineReveal
              lines={[
                <span key="a">Consulting</span>,
                <span key="b">that <em className="italic text-[#3E9BDC]">Empowers.</em></span>,
              ]}
            />
          </h1>

          <motion.p
            className="mt-8 max-w-2xl text-base md:text-lg text-white/75 leading-relaxed"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.65 }}
            data-testid="hero-subheadline"
          >
            Compliance, Capital Markets, and Trust Services for registered investment advisers,
            broker-dealers, insurance companies, fund sponsors, and trust companies.
          </motion.p>

          <motion.div
            className="mt-10 flex flex-wrap gap-4"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.85 }}
          >
            <Link to="/contact" data-testid="hero-consultation-button" className="btn-gold">
              Request a Consultation <ArrowRight size={16} />
            </Link>
            <a href="#practices" data-testid="hero-practices-button" className="btn-outline-light">
              Explore Our Practices
            </a>
          </motion.div>
        </div>
      </section>

      <Marquee items={MARQUEE_ITEMS} />

      <section id="practices" className="py-20 md:py-28 lg:py-32" data-testid="practices-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 md:px-12">
          <Reveal>
            <Kicker>Three Core Practices</Kicker>
            <h2 className="mt-5 font-serif font-bold text-3xl sm:text-4xl lg:text-5xl text-[#0A2B3D] tracking-tight max-w-3xl leading-[1.15]">
              One firm. Three disciplines. A single standard of rigor.
            </h2>
          </Reveal>

          <div className="mt-16 flex flex-col gap-6">
            {PRACTICES.map((p, i) => (
              <Reveal key={p.slug} delay={i * 0.08}>
                <Link
                  to={p.slug}
                  data-testid={`practice-card-${p.slug.slice(1)}`}
                  className={`group grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 items-start p-8 md:p-12 border border-[#0A2B3D]/12 bg-white hover:border-[#0A2B3D]/40 transition-all duration-500 hover:shadow-2xl ${i === 1 ? "md:ml-16" : i === 2 ? "md:ml-32" : ""}`}
                >
                  <div className="md:col-span-9">
                    <h3 className="font-serif font-bold text-2xl md:text-3xl text-[#0A2B3D]">{p.name}</h3>
                    <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.18em] text-[#3E9BDC]">{p.audience}</p>
                    <p className="mt-4 text-[#475569] leading-relaxed max-w-xl">{p.summary}</p>
                    <div className="mt-5 flex flex-wrap gap-2">
                      {p.tags.map((t) => (
                        <span key={t} className="font-mono text-[10px] uppercase tracking-[0.15em] px-3 py-1.5 bg-[#EBF3F8] text-[#0A2B3D]">{t}</span>
                      ))}
                    </div>
                  </div>
                  <div className="md:col-span-3 flex md:justify-end items-center">
                    <span className="inline-flex items-center gap-2 text-sm font-medium tracking-[0.1em] uppercase text-[#0A2B3D] group-hover:text-[#3E9BDC] transition-colors duration-300">
                      Explore <ArrowUpRight size={18} className="transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1" />
                    </span>
                  </div>
                </Link>
                {p.related && (
                  <div
                    className={`flex flex-wrap items-center gap-x-7 gap-y-2 px-8 md:px-12 py-4 border border-t-0 border-[#0A2B3D]/12 bg-[#EBF3F8] ${i === 1 ? "md:ml-16" : i === 2 ? "md:ml-32" : ""}`}
                    data-testid="related-services-strip"
                  >
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#475569]">Also under Compliance</span>
                    {p.related.map((r) => (
                      <Link
                        key={r.slug}
                        to={r.slug}
                        data-testid={`related-link-${r.slug.slice(1)}`}
                        className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0A2B3D] hover:text-[#3E9BDC] transition-colors"
                      >
                        {r.label} <ArrowUpRight size={14} />
                      </Link>
                    ))}
                  </div>
                )}
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#EBF3F8] py-20 md:py-28" data-testid="why-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 md:px-12">
          <Reveal>
            <Kicker>Why American Elm</Kicker>
            <div className="mt-5 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
              <h2 className="lg:col-span-7 font-serif font-bold text-3xl sm:text-4xl lg:text-5xl text-[#0A2B3D] tracking-tight leading-[1.15]">
                We seek to empower our partners to reach their fullest potential.
              </h2>
              <p className="lg:col-span-5 text-[#475569] leading-relaxed">
                A customized approach: we assess your goals and provide support in the areas you need
                most. In conjunction with our affiliate, American Elm Distribution Partners, we stand
                as a leading managing broker-dealer and versatile multi-product platform.
              </p>
            </div>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {WHY_AEA.map((w, i) => (
              <Reveal key={w.title} delay={i * 0.07}>
                <div className="h-full bg-white p-8 border border-[#0A2B3D]/10 hover:border-[#3E9BDC]/60 transition-colors duration-500" data-testid={`why-card-${i}`}>
                  <h3 className="font-serif font-bold text-xl text-[#0A2B3D]">{w.title}</h3>
                  <p className="mt-3 text-sm text-[#475569] leading-relaxed">{w.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#082838] text-white py-20 md:py-28" data-testid="expert-witness-spotlight">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-7">
            <Kicker light>Expert Witness Services</Kicker>
            <h2 className="mt-5 font-serif font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.15]">
              Testimony grounded in two decades of regulatory practice.
            </h2>
            <p className="mt-6 text-white/70 leading-relaxed max-w-xl">
              Led by Partner & Chief Compliance Officer Jessica Murray — member of the Securities
              Expert Roundtable and 11 Canterbury — our experts provide analysis and testimony for
              arbitration and regulatory matters across the investment and insurance industries.
            </p>
            <Link to="/expert-witness" data-testid="spotlight-expert-witness-button" className="btn-outline-light mt-8">
              Expert Witness Services <ArrowRight size={16} />
            </Link>
          </Reveal>
          <Reveal className="lg:col-span-5" delay={0.15}>
            <div className="border border-white/15 p-8 md:p-10 bg-[#0A2B3D]">
              <Scale className="text-[#3E9BDC]" size={28} />
              <p className="mt-5 eyebrow text-[#A9CFE2]">Why Experience Matters</p>
              <p className="mt-4 font-serif font-bold text-2xl text-white leading-snug" data-testid="expert-statement">
                Expert witness consulting can make a serious difference in your case.
              </p>
              <p className="mt-4 text-sm text-white/60 leading-relaxed">
                More than twenty years interpreting and applying the rules of FINRA, the SEC, and
                Departments of Insurance.
              </p>
              <div className="mt-6 pt-6 border-t border-white/10 flex flex-wrap gap-x-8 gap-y-2 font-mono text-[11px] uppercase tracking-[0.18em] text-[#A9CFE2]">
                <span>FINRA</span><span>SEC</span><span>Departments of Insurance</span><span>Arbitration</span>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <OfficeBand />

      <section className="bg-[#A9CFE2] py-20 md:py-28" data-testid="home-contact-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-5">
            <Kicker>Contact Us</Kicker>
            <h2 className="mt-5 font-serif font-bold text-3xl sm:text-4xl lg:text-5xl text-[#0A2B3D] tracking-tight leading-[1.15]">
              Start the conversation.
            </h2>
            <p className="mt-6 text-[#0A2B3D]/75 leading-relaxed max-w-md">
              Please fill out the consultation form and a member of our team will contact you to
              discuss your needs.
            </p>
            <div className="mt-8 text-sm text-[#0A2B3D] leading-relaxed font-medium">
              104 Congress Street, Suite 404<br />Portsmouth, NH 03801
            </div>
            <a href={CALENDLY_URL} target="_blank" rel="noreferrer" data-testid="home-calendly-link" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#0A2B3D] hover:text-[#082838] underline underline-offset-4">
              <CalendarClock size={16} /> Prefer to talk? Schedule a 15-minute call
            </a>
          </Reveal>
          <Reveal className="lg:col-span-7" delay={0.1}>
            <ContactForm defaultTopic="General Inquiry" />
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Home;
